import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Statistic,
  Card,
  Row,
  Col,
  Collapse,
  Tag,
  InputNumber,
  Flex,
  Segmented,
} from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import useData from "../../../hooks/useData";
import "dayjs/locale/ru";
import "dayjs/locale/ka";
import { PlusCircleOutlined } from "@ant-design/icons";
import NumberFormat from "../../../utils/NumberFormat";
import { AiFillPieChart } from "react-icons/ai";
import * as Icon from "react-feather";

const { RangePicker } = DatePicker;
const { Panel } = Collapse;

const { Option } = Select;

const TransactionsTable = () => {
  const { i18n } = useTranslation();
  const { language } = i18n;
  dayjs.locale(language);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const startOfMonth = dayjs().startOf("month").format("YYYY-MM-DD");
  const endOfMonth = dayjs().format("YYYY-MM-DD");

  const [filters, setFilters] = useState({
    search: "",
    startDate: startOfMonth,
    endDate: endOfMonth,
    transactionType: undefined,
    type: undefined,
  });
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [statisticsVisible, setStatisticsVisible] = useState(false);

  const { t } = useTranslation();
  const {
    data: tempData,
    isLoading,
    update,
  } = useData(
    `/finance/finance?page=${pagination.current}&limit=${pagination.pageSize}&range=${filters.startDate}&range=${filters.endDate}&transactionType=${filters.transactionType}&type=${filters.type}&search=${filters.search}`,
    {},
  );
  useEffect(() => {
    if (tempData?.pagination) {
      setData(tempData);
      setPagination(tempData.pagination);
    }
  }, [tempData]);

  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [filterForm] = Form.useForm();

  const handleFilterSubmit = (values) => {
    setFilters({
      search: values.search,
      transactionType: values.transactionType,
      type: values.type,
      startDate: values.range
        ? values.range[0].format("YYYY-MM-DD")
        : undefined,
      endDate: values.range ? values.range[1].format("YYYY-MM-DD") : undefined,
    });
    setFiltersVisible(false);
  };

  const columns = [
    {
      title: "Дата",
      dataIndex: "date",
      key: "date",
      render: (text) => dayjs(text).format("DD MMMM YYYY"),
    },
    {
      title: "Сумма",
      dataIndex: "price",
      key: "price",
      render: (text, row) =>
        row.type === "income" ? (
          <div style={{ color: "#3f8600", fontSize: 20 }}>
            + {NumberFormat(text)} ₾
          </div>
        ) : (
          <div style={{ fontSize: 20 }}>- {NumberFormat(text)} ₾</div>
        ),
    },
    {
      title: "Тип транзакции",
      dataIndex: "transactionType",
      key: "transactionType",
      render: (text) => (text === "bank" ? "Банк" : "Наличные"),
      sorter: (a, b) => a.transactionType.localeCompare(b.transactionType),
    },
    {
      title: "Коментарий",
      dataIndex: "contractId",
      key: "contractId",
    },
    {
      title: "Тип",
      dataIndex: "type",
      key: "type",
      render: (text) => (text === "income" ? "Доход" : "Расход"),
      sorter: (a, b) => a.type.localeCompare(b.type),
    },
  ];

  const handleAddTransaction = async () => {
    await update({
      ...form.getFieldsValue(),
    });
    setModalVisible(false);
    form.resetFields();
    form.setFieldValue("transactionType", "bank");
    form.setFieldValue("date", dayjs());
    form.setFieldValue("type", "outcome");
  };

  useEffect(() => {
    form.setFieldValue("transactionType", "bank");
    form.setFieldValue("type", "outcome");
    form.setFieldValue("date", dayjs());
  }, [form]);

  const handleChangeTable = async (pagination) => {
    setPagination(pagination);
  };

  const localize = (value) => {
    switch (value) {
      case "bank":
        return "Банк";
      case "cash":
        return "Наличные";
      case "income":
        return "Доход";
      case "outcome":
        return "Расход";
      default:
        return value;
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>{t("nav.Finance")}</h4>
            </div>
          </div>
          <div className="page-btn">
            <Flex>
              <a
                type="primary"
                onClick={() => setStatisticsVisible(true)}
                className="btn"
              >
                <AiFillPieChart className="me-2" size={22} /> Статистика
              </a>
              <a
                type="primary"
                onClick={() => setModalVisible(true)}
                className="btn btn-added"
              >
                <PlusCircleOutlined className="me-2" size={22} /> Добавить
                запись
              </a>
            </Flex>
          </div>
        </div>
        <Modal
          open={statisticsVisible}
          onCancel={() => setStatisticsVisible(false)}
          width={800}
          footer={false}
        >
          <Row gutter={16} style={{ marginBottom: 20 }}>
            <Col span={12}>
              <Card>
                <Statistic
                  title="Сейчас в кассе"
                  value={data?.allTime?.cash}
                  precision={2}
                  prefix={"₾"}
                  valueStyle={{ color: "#56af02" }}
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card>
                <Statistic
                  title="Сейчас на счету в банке"
                  value={data?.allTime?.bank}
                  precision={2}
                  prefix="₾"
                  valueStyle={{ color: "#ffb31a" }}
                />
              </Card>
            </Col>
          </Row>

          <Row gutter={16} style={{ marginBottom: 20 }}>
            <Col span={12}>
              <Card title="Всего доход за выбранный период">
                <Statistic
                  title="Общий доход"
                  value={data?.periodStats?.income?.total}
                  precision={2}
                  suffix="₾"
                  valueStyle={{ color: "#72ee00" }}
                />
                <Statistic
                  title="Наличными"
                  value={data?.periodStats?.income?.cash}
                  precision={2}
                  suffix="₾"
                  style={{ marginTop: 10 }}
                />
                <Statistic
                  title="Переводом"
                  value={data?.periodStats?.income?.bank}
                  precision={2}
                  suffix="₾"
                  style={{ marginTop: 10 }}
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card title="Всего расход за выбранный период">
                <Statistic
                  title="Общий расход"
                  value={data?.periodStats?.outcome?.total}
                  precision={2}
                  suffix="₾"
                  valueStyle={{ color: "#ff4d4f" }}
                />
                <Statistic
                  title="Наличными"
                  value={data?.periodStats?.outcome?.cash}
                  precision={2}
                  suffix="₾"
                  style={{ marginTop: 10 }}
                />
                <Statistic
                  title="Переводом"
                  value={data?.periodStats?.outcome?.bank}
                  precision={2}
                  suffix="₾"
                  style={{ marginTop: 10 }}
                />
              </Card>
            </Col>
          </Row>
        </Modal>

        <Collapse
          activeKey={filtersVisible ? ["1"] : []}
          onChange={() => setFiltersVisible(!filtersVisible)}
        >
          <Panel
            header={
              <>
                <Flex gap={12} justify={"space-between"}>
                  Фильтры
                  <div>
                    {Object.values(filters).map((value, index) =>
                      value ? (
                        <Tag key={`tag_${index}`}>{localize(value)}</Tag>
                      ) : null,
                    )}
                  </div>
                </Flex>
              </>
            }
            key="1"
          >
            <Form
              form={filterForm}
              layout="vertical"
              onFinish={handleFilterSubmit}
              initialValues={{
                ...filters,
                range: [dayjs(startOfMonth), dayjs(endOfMonth)],
              }}
            >
              <Row gutter={16}>
                <Col md={5} xs={12}>
                  <Form.Item name="search" label="Поиск">
                    <Input placeholder="Поиск" />
                  </Form.Item>
                </Col>
                <Col md={5} xs={12}>
                  <Form.Item name="transactionType" label="Тип транзакции">
                    <Select allowClear>
                      <Option value="bank">Банк</Option>
                      <Option value="cash">Наличные</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col md={4} xs={12}>
                  <Form.Item name="type" label="Тип">
                    <Select allowClear>
                      <Option value="income">Доход</Option>
                      <Option value="outcome">Расход</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col md={6} xs={24}>
                  <Form.Item name="range" label="Диапазон дат">
                    <RangePicker
                      format="YYYY-MM-DD"
                      defaultValue={[dayjs(startOfMonth), dayjs(endOfMonth)]}
                      allowClear={false}
                      style={{ width: "100%" }}
                      maxDate={dayjs()}
                      locale={language}
                    />
                  </Form.Item>
                </Col>
                <Col
                  md={4}
                  xs={24}
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <Button type="primary" htmlType="submit">
                    Применить фильтр
                  </Button>
                </Col>
              </Row>
            </Form>
          </Panel>
        </Collapse>
        <div className={"table-responsive"}>
          <Table
            columns={columns}
            dataSource={data.transactions}
            rowKey="_id"
            style={{ marginTop: 20 }}
            pagination={pagination}
            onChange={handleChangeTable}
            loading={isLoading}
            size={"small"}
          />
        </div>

        <Modal
          title="Добавить доход/расход"
          visible={modalVisible}
          onCancel={() => setModalVisible(false)}
          footer={null}
          width={600}
        >
          <Form form={form} onFinish={handleAddTransaction} layout="vertical">
            <Form.Item
              name="date"
              label="Дата"
              rules={[{ required: true, message: "Выберите дату" }]}
            >
              <DatePicker
                format="YYYY-MM-DD"
                size={"large"}
                style={{ width: "100%" }}
                maxDate={dayjs()}
                defaultValue={dayjs()}
              />
            </Form.Item>
            <Form.Item
              name="price"
              label="Сумма"
              rules={[{ required: true, message: "Введите сумму" }]}
            >
              <InputNumber
                prefix={"₾"}
                size={"large"}
                style={{ width: "100%" }}
                keyboard={false}
              />
            </Form.Item>
            <Form.Item
              name="transactionType"
              label="Тип транзакции"
              rules={[{ required: true, message: "Выберите тип" }]}
            >
              <Segmented
                options={[
                  {
                    label: (
                      <Flex align={"center"} gap={6}>
                        <Icon.Server size={12} /> Банк
                      </Flex>
                    ),
                    value: "bank",
                  },
                  {
                    label: (
                      <Flex align={"center"} gap={6}>
                        <Icon.User size={12} /> Наличние
                      </Flex>
                    ),
                    value: "cash",
                  },
                ]}
                size={"large"}
              />
            </Form.Item>
            <Form.Item
              name="type"
              label="Тип"
              rules={[{ required: true, message: "Выберите тип" }]}
            >
              <Segmented
                options={[
                  {
                    label: (
                      <Flex align={"center"} gap={6}>
                        <Icon.CornerDownLeft color={"green"} size={12} /> Доход
                      </Flex>
                    ),
                    value: "income",
                  },
                  {
                    label: (
                      <Flex align={"center"} gap={6}>
                        <Icon.CornerUpRight color={"red"} size={12} /> Расход
                      </Flex>
                    ),
                    value: "outcome",
                  },
                ]}
                size={"large"}
              />
            </Form.Item>
            <Form.Item name="contractId" label="Описание">
              <Input.TextArea />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit">
                Добавить
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default TransactionsTable;
